/**
 * @file ConversationListEmpty.tsx
 * @description Пустые состояния списка диалогов (chat-window-design.md §5):
 * свой текст у «Всех», «Моих», «Архива», «Без ответственного» и поиска.
 * Реестр по виду пустоты, а не каскад в разметке.
 */

import { Archive, Inbox, SearchX, UserCheck, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "../ui/button";
import { EmptyState } from "../ui/EmptyState";
import { CHAT_TEXT } from "./chat-texts";
import type { ListEmptyKind } from "./list-empty-kind";

interface Props {
  kind: ListEmptyKind;
  search: string;
  canDispatch: boolean;
  onOpenAll: () => void;
  onResetUnassigned: () => void;
}

interface EmptyMeta {
  icon: LucideIcon;
  title: (p: Props) => string;
  description?: (p: Props) => string | undefined;
  action?: (p: Props) => ReactNode;
}

const EMPTY_META: Record<ListEmptyKind, EmptyMeta> = {
  all: { icon: Inbox, title: () => CHAT_TEXT.emptyAll },
  mine: {
    icon: UserCheck,
    title: () => CHAT_TEXT.emptyMine,
    description: (p) => (p.canDispatch ? CHAT_TEXT.emptyMineHint : undefined),
    action: (p) =>
      p.canDispatch ? (
        <Button variant="outline" className="min-h-12 lg:min-h-10" onClick={p.onOpenAll}>
          {CHAT_TEXT.openAll}
        </Button>
      ) : null,
  },
  archive: { icon: Archive, title: () => CHAT_TEXT.emptyArchive },
  unassigned: {
    icon: UserCheck,
    title: () => CHAT_TEXT.emptyUnassigned,
    action: (p) => (
      <Button variant="outline" className="min-h-12 lg:min-h-10" onClick={p.onResetUnassigned}>
        {CHAT_TEXT.resetFilter}
      </Button>
    ),
  },
  search: { icon: SearchX, title: (p) => CHAT_TEXT.notFound(p.search.trim()) },
};

export function ConversationListEmpty(props: Props) {
  const meta = EMPTY_META[props.kind];
  return (
    <EmptyState
      className="m-3 border-none"
      icon={meta.icon}
      variant={props.kind === "search" || props.kind === "unassigned" ? "filtered" : "empty"}
      title={meta.title(props)}
      description={meta.description?.(props)}
      action={meta.action?.(props)}
    />
  );
}
